import React, { ReactNode, useState } from "react"

import {
  ChevronDownIcon,
  ChevronRightIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/solid"

import Panel from "~tailwindui/Basics/Panel"

import classNames from "src/classNames"

import useIsMobile from "hooks/useIsMobile"

import Paginator from "./Paginator"

export type TableProps = {
  data: any
  title?: string | ReactNode
  subtitle?: string
  collapsible?: boolean
  collapsedByDefault?: boolean
  rowCallback?: (record) => void
  changePage?: (page: number) => void
  cols: RowType[]
}

type RowType = {
  header: string
  value: (record: any) => ReactNode
  hidden?: boolean
}

const Table: React.FC<TableProps> = ({
  data,
  changePage = () => undefined,
  title,
  subtitle,
  collapsible = true,
  collapsedByDefault = false,
  rowCallback,
  cols,
}) => {
  const isMobile = useIsMobile()
  const records = data.data || data
  const { currentPage, totalPages } = data

  const [collapsed, setCollapsed] = useState(
    currentPage === 1 && collapsedByDefault
  )

  const visibleCols = cols.filter(col => !col.hidden)
  return (
    <Panel className="mb-6 mt-4 px-5 pt-3 pb-5">
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          {title && (
            <h2 className="my-4 flex flex-col gap-4 text-xl font-medium leading-6 text-gray-700 sm:flex-row sm:items-center ">
              <span>{title}</span>
              {collapsible && (
                <span className="inline-block text-sm text-gray-500 hover:text-gray-600">
                  {collapsed ? (
                    <button
                      className="flex items-center gap-2 uppercase"
                      onClick={() => setCollapsed(false)}
                    >
                      Expand
                      <ChevronDownIcon className="h-5 w-5" />
                    </button>
                  ) : (
                    <button
                      className="flex items-center gap-2 uppercase"
                      onClick={() => setCollapsed(true)}
                    >
                      Collapse
                      <ChevronUpIcon className="h-5 w-5" />
                    </button>
                  )}
                </span>
              )}
            </h2>
          )}
          {subtitle && <p className="mt-2 text-sm text-gray-700">{subtitle}</p>}
        </div>
        <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none"></div>
      </div>

      <div className="flow-root px-4 sm:px-6 lg:px-8">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 align-middle sm:min-w-full">
            <table
              className={classNames(
                "border-separate border-spacing-0 sm:min-w-full",
                collapsed && "hidden"
              )}
            >
              <thead className="hidden sm:table-header-group">
                <tr>
                  <th
                    key={visibleCols[0].header}
                    scope="col"
                    className="top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 py-3.5 pl-2 pr-3 text-left text-sm font-medium text-gray-900 backdrop-blur backdrop-filter"
                  >
                    {visibleCols[0].header}
                  </th>
                  {visibleCols.slice(1).map(col => (
                    <th
                      key={col.header}
                      scope="col"
                      className="top-0 z-10 border-b border-gray-300 bg-white bg-opacity-75 px-3 py-3.5 text-left text-sm font-medium text-gray-900 backdrop-blur backdrop-filter sm:table-cell"
                    >
                      {col.header}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {records.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleCols.length}
                      className="py-8 pl-4 italic text-gray-900"
                    >
                      No records found
                    </td>
                  </tr>
                ) : (
                  records.map((record, recordIdx) => {
                    return (
                      <tr
                        tabIndex={rowCallback ? 0 : undefined}
                        role={rowCallback ? "button" : undefined}
                        onKeyDown={e => {
                          if (e.currentTarget !== e.target) return
                          if (
                            (e.key === "Enter" || e.key === " ") &&
                            rowCallback
                          ) {
                            e.preventDefault() // prevent page scroll on Space
                            rowCallback(record)
                          }
                        }}
                        key={record.id || JSON.stringify(record)}
                        onClick={() => {
                          if (!isMobile && rowCallback) rowCallback(record)
                        }}
                        className={classNames(
                          recordIdx !== records.length - 1 &&
                            "border-b border-gray-300 sm:border-none",
                          "flex flex-col  py-4 sm:table-row sm:py-0 sm:hover:bg-gray-200/50"
                        )}
                      >
                        <td
                          className={classNames(
                            "whitespace-normal py-2 text-sm font-medium text-gray-500 sm:py-4 sm:pl-2 sm:pr-3 sm:text-gray-700",
                            recordIdx !== records.length - 1 &&
                              "sm:border-b sm:border-gray-300"
                          )}
                        >
                          {/* inline header for mobile */}
                          <span className="mb-1 block text-sm font-semibold uppercase text-gray-700 sm:hidden">
                            {visibleCols[0].header}
                          </span>
                          <div className="">{visibleCols[0].value(record)}</div>
                        </td>
                        {visibleCols
                          .slice(1)
                          .map(col => col.value(record))
                          .map((val, colIdx) => (
                            <td
                              key={`${record.id}-col-${colIdx}`}
                              className={classNames(
                                "whitespace-normal py-2 text-sm text-gray-500 sm:table-cell sm:px-3 sm:py-4",
                                recordIdx !== records.length - 1
                                  ? "sm:border-b sm:border-gray-300"
                                  : ""
                              )}
                            >
                              <span className="mb-1 block text-sm font-semibold uppercase text-gray-700 sm:hidden">
                                {visibleCols[colIdx + 1].header}
                              </span>
                              <div className="">{val}</div>
                            </td>
                          ))}
                        {rowCallback && isMobile && (
                          <td>
                            <button
                              type="button"
                              onClick={() => rowCallback(record)}
                              className="mb-2 flex max-w-max cursor-pointer items-center justify-center rounded-md border border-solid py-2 px-4 text-gray-500 text-gray-500 shadow active:bg-gray-200"
                            >
                              Details
                              <ChevronRightIcon className="h-6 w-6" />
                            </button>
                          </td>
                        )}
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {records.length > 0 && currentPage && !collapsed && (
          <Paginator
            currentPage={currentPage}
            totalPages={totalPages}
            changePage={changePage}
          />
        )}
      </div>
    </Panel>
  )
}
export default Table
